import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../../core/services/application/AuthService';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AuthLayout from '../../../components/auth/AuthLayout';
import PasswordInput from '../../../components/auth/PasswordInput';
import { getErrorMessage, toError } from '../../../core/utils/errorMessage';
import { Logger } from '../../../core/utils/logger';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    // Supabase processes the recovery link in the URL when the client boots
    // (implicit flow), so a valid link yields a session by the time getSession
    // resolves — even if the URL itself no longer carries the token.
    const checkSession = async () => {
      const { session } = await AuthService.getCurrentSession();
      if (cancelled) return;
      setIsCheckingSession(false);
      setHasSession(Boolean(session));
      if (!session) {
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    };

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await AuthService.updatePassword(password);

      if (error) throw new Error(error);

      setMessage('Password updated successfully!');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000);
    } catch (err: unknown) {
      Logger.error('Password reset error:', { error: toError(err) });
      setError(getErrorMessage(err, 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      {error && (
        <div className="mb-6 p-3 bg-red-500/20 text-red-300 dark:text-red-700 rounded-xl text-sm backdrop-blur-sm border border-red-400/30">
          {error}
        </div>
      )}

      {message && (
        <div className="mb-6 p-3 bg-green-500/20 text-green-300 dark:text-green-700 rounded-xl text-sm backdrop-blur-sm border border-green-400/30">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-5">
          <PasswordInput
            id="password"
            placeholder="New Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="mb-6">
          <PasswordInput
            id="confirmPassword"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          className="w-full py-4 px-5 bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90 text-base font-semibold rounded-xl transition duration-200 flex items-center justify-center min-h-[52px]"
          disabled={isLoading || isCheckingSession || !hasSession}
        >
          {isLoading ? (
            <span className="flex items-center">
              <LoadingSpinner />
              <span className="ml-2">Updating Password...</span>
            </span>
          ) : (
            'Save password'
          )}
        </button>
      </form>

      <div className="mt-6">
        <button
          onClick={() => navigate('/auth/login')}
          className="text-base text-white/80 hover:text-white focus:outline-none transition duration-200"
        >
          Back to sign in
        </button>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
